import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FileText, Loader2, RefreshCw, Save, Mail, CheckCircle, Download } from 'lucide-react';
import {
  getSuperAdminInvoicing,
  putSuperAdminEurRate,
  patchQuarterlyInvoicePaid,
  postQuarterlyInvoiceOfficialEmail,
  getQuarterlyInvoicePdfSuperAdmin,
} from '../../../redux/api';
import { formatInvoiceEur, formatInvoiceRsd } from '../../../utils/invoiceDisplayFormat';
import {
  formatBillingYmdLocal,
  formatBillingInstantDate,
  formatBillingInstantDateTime,
  billingDateLocaleForLanguage,
} from '../../../utils/billingDateFormat';

const statusLabel = (s) => {
  switch (s) {
    case 'PENDING_PAYMENT':
      return 'Na čekanju uplate';
    case 'PAID':
      return 'Plaćeno (čeka zvanični mejl)';
    case 'OFFICIAL_SENT':
      return 'Zvanična potvrda poslata';
    default:
      return s || '—';
  }
};

const SuperAdminInvoicingView = () => {
  const { i18n } = useTranslation();
  const dateLoc = billingDateLocaleForLanguage(i18n.language);
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rateInput, setRateInput] = useState('');
  const [rateMeta, setRateMeta] = useState({ updatedAt: null, updatedBy: null });
  const [invoices, setInvoices] = useState([]);
  const [companyBalances, setCompanyBalances] = useState([]);
  const [rowBusy, setRowBusy] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getSuperAdminInvoicing();
      if (data.eurToRsdManual != null && Number.isFinite(data.eurToRsdManual)) {
        setRateInput(String(data.eurToRsdManual));
      } else {
        setRateInput('');
      }
      setRateMeta({
        updatedAt: data.rateUpdatedAt || null,
        updatedBy: data.rateUpdatedByEmail || null,
      });
      setInvoices(Array.isArray(data.quarterlyInvoices) ? data.quarterlyInvoices : []);
      setCompanyBalances(Array.isArray(data.companyBalances) ? data.companyBalances : []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error('Nemate pristup');
      return;
    }
    load();
  }, [isSuperAdmin, load]);

  const handleSaveRate = async (e) => {
    e.preventDefault();
    const n = Number(String(rateInput).replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Unesite pozitivan broj (npr. srednji kurs NBS za EUR).');
      return;
    }
    try {
      setSaving(true);
      await putSuperAdminEurRate(n);
      toast.success('Kurs je sačuvan. Koristi se u obračunu (Fakture) u aplikaciji.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  const setBusy = (id, v) => setRowBusy((prev) => ({ ...prev, [id]: v }));

  const handleDownloadPdf = async (inv) => {
    setBusy(inv._id, 'pdf');
    try {
      const { data } = await getQuarterlyInvoicePdfSuperAdmin(inv._id);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Preuzimanje PDF nije uspelo.');
    } finally {
      setBusy(inv._id, null);
    }
  };

  const handleMarkPaid = async (id) => {
    setBusy(id, 'paid');
    try {
      await patchQuarterlyInvoicePaid(id, {});
      toast.success('Označeno kao plaćeno.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Greška');
    } finally {
      setBusy(id, null);
    }
  };

  const handleSendOfficial = async (id) => {
    setBusy(id, 'mail');
    try {
      await postQuarterlyInvoiceOfficialEmail(id);
      toast.success('Zvanična potvrda poslata na mejl klijenta.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Slanje nije uspelo (proverite SMTP / SMTP_INVOICE_FROM).');
    } finally {
      setBusy(id, null);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto text-gray-100">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/20">
            <FileText className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Fakturisanje</h1>
            <p className="text-sm text-gray-400">
              Tromesečni obračun od 1. maja 2026. (maj–jul, avg–okt, nov–jan, feb–apr) — fakture ST-000001 …
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Osveži
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <form
          onSubmit={handleSaveRate}
          className="lg:col-span-1 bg-gray-800/80 border border-gray-700 rounded-xl p-5 space-y-4"
        >
          <h2 className="text-lg font-semibold text-white">Kurs EUR / RSD</h2>
          <p className="text-xs text-gray-500 leading-relaxed">
            Unesite srednji kurs sa izvoda / NBS-a. Koristi se pri kreiranju tromesečne fakture (procena RSD) i na
            stranici Fakture kod klijenta.
          </p>
          <div>
            <label htmlFor="eur-rsd-rate" className="block text-sm text-gray-400 mb-1">
              1 EUR = ___ RSD
            </label>
            <input
              id="eur-rsd-rate"
              type="text"
              inputMode="decimal"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white font-mono"
              placeholder="npr. 117.45"
              disabled={saving}
            />
          </div>
          <button
            type="submit"
            disabled={saving || loading}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sačuvaj kurs
          </button>
          {(rateMeta.updatedAt || rateMeta.updatedBy) && (
            <p className="text-xs text-gray-500">
              Poslednja izmena:{' '}
              {rateMeta.updatedAt ? formatBillingInstantDateTime(rateMeta.updatedAt, dateLoc) : '—'}
              {rateMeta.updatedBy ? ` · ${rateMeta.updatedBy}` : ''}
            </p>
          )}
        </form>

        <div className="lg:col-span-2 bg-gray-800/50 border border-gray-700 rounded-xl p-5 text-sm text-gray-400">
          <h3 className="text-white font-medium mb-2">Kako radi</h3>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>
              <strong className="text-gray-300">Naplata kreće od 1. maja 2026.</strong> Periodi su fiksna
              tromesečja: maj–jul, avgust–oktobar, novembar–januar, februar–april. Prvi mesec od registracije
              ostaje besplatan, ali ništa pre 1. maja se ne fakturiše.
            </li>
            <li>
              Faktura <span className="font-mono text-gray-300">ST-xxxxxx</span> nastaje kada se tromesečje
              završi (npr. prva za maj–jul posle 31. jula).
            </li>
            <li>
              Dok tromesečni period još traje, zaduženje je u tabeli ispod (obračun do današnjeg dana).
            </li>
            <li>Status „Na čekanju uplate“ — klijent dobija poziv na broj; kada vidite uplatu, označite „Plaćeno“.</li>
            <li>
              Zatim „Pošalji zvaničnu potvrdu“ — mejl sa <span className="font-mono">info@spintasker.com</span> (podesite{' '}
              <span className="font-mono">SMTP_INVOICE_FROM</span> u .env ako treba).
            </li>
            <li>Stari modul SUB- / beta uplate više nije u ovoj listi.</li>
          </ul>
        </div>
      </div>

      <div className="bg-gray-800/80 border border-gray-700 rounded-xl overflow-hidden mb-8">
        <div className="px-4 py-3 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Kompanije na plaćenom paketu — trenutno zaduženje</h2>
          <p className="text-xs text-gray-500 mt-1">
            Uključuje kompanije bez ST fakture dok tromesečni period još traje.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-700">
                <th className="px-4 py-3 font-medium">Kompanija</th>
                <th className="px-4 py-3 font-medium">Paket</th>
                <th className="px-4 py-3 font-medium">Besplatno do</th>
                <th className="px-4 py-3 font-medium">Naplata od</th>
                <th className="px-4 py-3 font-medium">Tekući period</th>
                <th className="px-4 py-3 font-medium">Zaduženje</th>
                <th className="px-4 py-3 font-medium">ST faktura</th>
              </tr>
            </thead>
            <tbody>
              {companyBalances.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Nema kompanija na plaćenom paketu (Starter i više).
                  </td>
                </tr>
              ) : (
                companyBalances.map((row) => {
                  const b = row.billing || {};
                  const period =
                    b.currentPeriodStartYmd && b.currentPeriodEndYmd
                      ? `${formatBillingYmdLocal(b.currentPeriodStartYmd, dateLoc)} — ${formatBillingYmdLocal(b.currentPeriodEndYmd, dateLoc)}`
                      : '—';
                  const stNote = b.pendingInvoicesEur > 0
                    ? 'Na čekanju (ST)'
                    : b.awaitingQuarterlyInvoice
                      ? b.nextQuarterlyInvoiceAfterYmd
                        ? `Posle ${formatBillingYmdLocal(b.nextQuarterlyInvoiceAfterYmd, dateLoc)}`
                        : 'Čeka kraj perioda'
                      : '—';
                  return (
                    <tr key={row._id} className="border-b border-gray-700/80 hover:bg-gray-700/30">
                      <td className="px-4 py-2 text-gray-200">
                        <div>{row.companyName}</div>
                        <div className="text-xs text-gray-500">{row.email}</div>
                      </td>
                      <td className="px-4 py-2 text-gray-300">{b.packageLabelSr || '—'}</td>
                      <td className="px-4 py-2 text-gray-400 text-xs whitespace-nowrap">
                        {b.freePeriodEndYmd ? formatBillingYmdLocal(b.freePeriodEndYmd, dateLoc) : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-400 text-xs whitespace-nowrap">
                        {b.billingStartsYmd ? formatBillingYmdLocal(b.billingStartsYmd, dateLoc) : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-400 text-xs whitespace-nowrap">{period}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        <span className="text-amber-300">{formatInvoiceEur(b.outstandingEur || 0)} EUR</span>
                        {row.outstandingRsdEstimate != null && (
                          <span className="block text-gray-500">
                            ≈ {formatInvoiceRsd(row.outstandingRsdEstimate)} RSD
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-400">{stNote}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-800/80 border border-gray-700 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Tromesečne fakture (ST-)</h2>
          {loading && <Loader2 className="w-5 h-5 animate-spin text-gray-400" />}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-700">
                <th className="px-4 py-3 font-medium">Broj</th>
                <th className="px-4 py-3 font-medium">Kompanija</th>
                <th className="px-4 py-3 font-medium">Period obračuna</th>
                <th className="px-4 py-3 font-medium">Datum izdavanja</th>
                <th className="px-4 py-3 font-medium">Iznos</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Poziv na broj</th>
                <th className="px-4 py-3 font-medium">Akcije / PDF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Još nema tromesečnih faktura. Pojaviće se automatski posle završetka tromesečja od 1. maja
                    2026. (prvo: maj–jul).
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => {
                  const comp = inv.companyId;
                  const compName = comp?.companyName || comp?.email || '—';
                  const busy = rowBusy[inv._id];
                  return (
                    <tr key={inv._id} className="border-b border-gray-700/80 hover:bg-gray-700/30">
                      <td className="px-4 py-2 font-mono text-xs text-emerald-300">{inv.invoiceNumber}</td>
                      <td className="px-4 py-2 text-gray-200">{compName}</td>
                      <td className="px-4 py-2 text-gray-400 whitespace-nowrap text-xs">
                        {formatBillingYmdLocal(inv.periodStartYmd, dateLoc)} —{' '}
                        {formatBillingYmdLocal(inv.periodEndYmd, dateLoc)}
                      </td>
                      <td className="px-4 py-2 text-gray-400 whitespace-nowrap">
                        {inv.issuedAt ? formatBillingInstantDate(inv.issuedAt, dateLoc) : '—'}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {inv.amountEur != null ? `${formatInvoiceEur(inv.amountEur)} EUR` : '—'}
                        {inv.amountRsdEstimate != null && (
                          <span className="block text-gray-500">
                            ≈ {formatInvoiceRsd(inv.amountRsdEstimate)} RSD
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">{statusLabel(inv.status)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-300 break-all max-w-[140px]">
                        {inv.invoiceNumber || '—'}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="flex flex-col sm:flex-row gap-1">
                          <button
                            type="button"
                            disabled={!!busy}
                            onClick={() => handleDownloadPdf(inv)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-600 hover:bg-gray-500 text-xs text-white"
                          >
                            {busy === 'pdf' ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3" />
                            )}
                            PDF
                          </button>
                          {inv.status === 'PENDING_PAYMENT' && (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() => handleMarkPaid(inv._id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-700/80 hover:bg-amber-600 text-xs text-white"
                            >
                              {busy === 'paid' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3 h-3" />
                              )}
                              Plaćeno
                            </button>
                          )}
                          {inv.status === 'PAID' && (
                            <button
                              type="button"
                              disabled={!!busy}
                              onClick={() => handleSendOfficial(inv._id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-700/80 hover:bg-blue-600 text-xs text-white"
                            >
                              {busy === 'mail' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Mail className="w-3 h-3" />
                              )}
                              Pošalji potvrdu
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminInvoicingView;
